import "./FincaFormModal.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { FincaFichaResponse } from "../../../shared/response/terceros/FincaFichaResponse";
import { FincaResumenResponse } from "../../../shared/response/terceros/FincaResumenResponse";
import { MunicipioResponse } from "../../../shared/response/comun/MunicipioResponse";
import { DepartamentoRegionResponse } from "../../../shared/response/comun/DepartamentoRegionResponse";
import { FincaPayload } from "../../../shared/services/api/payload/terceros/FincaPayload";
import { FincaSaveService } from "../../services/terceros/FincaSaveService";
import { DepartamentoListService } from "../../../shared/services/comun/DepartamentoListService";
import { MunicipioListService } from "../../../shared/services/comun/MunicipioListService";
import { DepartamentoRegionListService } from "../../../shared/services/comun/DepartamentoRegionListService";

export class FincaFormModal extends BaseSmallModalFormComponent<
  FincaResumenResponse,
  FincaPayload,
  FincaSaveService
> {
  private _nombreInput!: HTMLInputElement;
  private _tipoSelect!: HTMLSelectElement;
  private _haInput!: HTMLInputElement;
  private _haProdInput!: HTMLInputElement;
  private _deptoSelect!: HTMLSelectElement;
  private _muniSelect!: HTMLSelectElement;
  private _regionSelect!: HTMLSelectElement;

  private _allMunis: MunicipioResponse[] = [];
  private _allRegiones: DepartamentoRegionResponse[] = [];

  constructor(
    private readonly _clienteId: string,
    private readonly _onSaved: (finca: FincaResumenResponse) => void,
    private readonly _item?: FincaFichaResponse,
  ) {
    super(FincaSaveService.instance);
  }

  protected get title(): string { return this._item ? "Editar finca" : "Nueva finca"; }

  private _esc(v: string): string {
    return v.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  }

  protected renderBody(): string {
    const nombre = this._item?.nombreFinca ?? "";
    const tipo   = this._item?.tipoFinca != null ? String(this._item.tipoFinca) : "";
    const ha     = this._item?.hectareasFinca     ? String(this._item.hectareasFinca)     : "";
    const haProd = this._item?.hectareasProductivas ? String(this._item.hectareasProductivas) : "";

    return `
      <div class="fi-form-fields">
        <div class="cl-form-group">
          <label class="cl-form-label" for="fi-nombre">
            Nombre <span class="cl-required">*</span>
          </label>
          <input
            id="fi-nombre"
            class="sx-input"
            type="text"
            placeholder="Nombre de la finca"
            maxlength="500"
            value="${this._esc(nombre)}"
          />
        </div>

        <div class="cl-form-group">
          <label class="cl-form-label" for="fi-tipo">Tipo</label>
          <select id="fi-tipo" class="sx-input">
            <option value="">— Seleccionar —</option>
            <option value="0" ${tipo === "0" ? "selected" : ""}>Agricultura</option>
            <option value="1" ${tipo === "1" ? "selected" : ""}>Ganadería</option>
          </select>
        </div>

        <div class="fi-row2">
          <div class="cl-form-group">
            <label class="cl-form-label" for="fi-ha">Hectáreas totales</label>
            <input id="fi-ha" class="sx-input sx-input--numeric" type="number" min="0" step="0.01" placeholder="0.00" value="${ha}" />
          </div>
          <div class="cl-form-group">
            <label class="cl-form-label" for="fi-ha-prod">Hectáreas productivas</label>
            <input id="fi-ha-prod" class="sx-input sx-input--numeric" type="number" min="0" step="0.01" placeholder="0.00" value="${haProd}" />
          </div>
        </div>

        <div class="cl-form-group">
          <label class="cl-form-label" for="fi-depto">Departamento</label>
          <select id="fi-depto" class="sx-input">
            <option value="">Cargando…</option>
          </select>
        </div>

        <div class="cl-form-group">
          <label class="cl-form-label" for="fi-muni">Municipio</label>
          <select id="fi-muni" class="sx-input" disabled>
            <option value="">— Selecciona departamento —</option>
          </select>
        </div>

        <div class="cl-form-group">
          <label class="cl-form-label" for="fi-region">Región</label>
          <select id="fi-region" class="sx-input" disabled>
            <option value="">— Selecciona departamento —</option>
          </select>
        </div>
      </div>
    `;
  }

  protected override async onAfterRender(el: HTMLDivElement): Promise<void> {
    this._nombreInput  = el.querySelector<HTMLInputElement>("#fi-nombre")!;
    this._tipoSelect   = el.querySelector<HTMLSelectElement>("#fi-tipo")!;
    this._haInput      = el.querySelector<HTMLInputElement>("#fi-ha")!;
    this._haProdInput  = el.querySelector<HTMLInputElement>("#fi-ha-prod")!;
    this._deptoSelect  = el.querySelector<HTMLSelectElement>("#fi-depto")!;
    this._muniSelect   = el.querySelector<HTMLSelectElement>("#fi-muni")!;
    this._regionSelect = el.querySelector<HTMLSelectElement>("#fi-region")!;

    this._nombreInput.addEventListener("input", () => this._validate());
    this._deptoSelect.addEventListener("change", () => this._onDeptoChange());

    this._validate();
    await this._loadCatalogs();
  }

  protected buildPayload(): FincaPayload {
    const ha     = this._haInput.value;
    const haProd = this._haProdInput.value;
    const tipo   = this._tipoSelect.value;

    return {
      id:                  this._item?.id ?? null,
      clienteId:           this._clienteId,
      nombre:              this._nombreInput.value.trim(),
      tipo:                tipo !== "" ? Number(tipo) : null,
      hectareasFinca:      ha     !== "" ? Number(ha)     : null,
      hectareasProductivas: haProd !== "" ? Number(haProd) : null,
      departamentoId:      this._deptoSelect.value  || null,
      municipioId:         this._muniSelect.value   || null,
      departamentoRegionId: this._regionSelect.value || null,
    };
  }

  protected override onSaved(item: FincaResumenResponse): void {
    this._onSaved(item);
    this.dispose();
  }

  private _validate(): void {
    this.setSaveDisabled(this._nombreInput.value.trim().length === 0);
  }

  private async _loadCatalogs(): Promise<void> {
    try {
      const [deptos, munis, regiones] = await Promise.all([
        DepartamentoListService.instance.get(),
        MunicipioListService.instance.get(),
        DepartamentoRegionListService.instance.get(),
      ]);

      this._allMunis    = munis;
      this._allRegiones = regiones;

      this._deptoSelect.innerHTML =
        '<option value="">— Departamento —</option>' +
        deptos.map((d) => `<option value="${d.id}">${d.nombre}</option>`).join("");

      if (this._item?.departamentoNombre) {
        const depto = deptos.find((d) => d.nombre === this._item!.departamentoNombre);
        if (depto) {
          this._deptoSelect.value = depto.id;
          this._onDeptoChange();

          if (this._item.municipioNombre) {
            const muni = this._allMunis.find(
              (m) => m.departamentoId === depto.id && m.nombre === this._item!.municipioNombre,
            );
            if (muni) this._muniSelect.value = muni.id;
          }

          if (this._item.departamentoRegionNombre) {
            const region = this._allRegiones.find(
              (r) => r.departamentoId === depto.id && r.nombre === this._item!.departamentoRegionNombre,
            );
            if (region) this._regionSelect.value = region.id;
          }
        }
      }
    } catch {
      this._deptoSelect.innerHTML = '<option value="">Error al cargar</option>';
    }
  }

  private _onDeptoChange(): void {
    const deptoId = this._deptoSelect.value;

    const munis = deptoId
      ? this._allMunis.filter((m) => m.departamentoId === deptoId)
      : [];
    this._muniSelect.innerHTML =
      '<option value="">— Municipio —</option>' +
      munis.map((m) => `<option value="${m.id}">${m.nombre}</option>`).join("");
    this._muniSelect.disabled = !deptoId;

    const regiones = deptoId
      ? this._allRegiones.filter((r) => r.departamentoId === deptoId)
      : [];
    this._regionSelect.innerHTML =
      '<option value="">— Región —</option>' +
      regiones.map((r) => `<option value="${r.id}">${r.nombre}</option>`).join("");
    this._regionSelect.disabled = !deptoId;
  }
}
